import React, { useEffect, useState } from 'react';
import { StickyNote, Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { salesAPI } from '@/lib/api';

export const NOTE_CATEGORIES = [
    { id: 'fermeture',  label: 'Fermeture',   color: 'bg-red-100 text-red-700 border-red-200' },
    { id: 'panne',      label: 'Panne',       color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { id: 'absence',    label: 'Absence',     color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { id: 'ferie',      label: 'Férié',       color: 'bg-violet-100 text-violet-700 border-violet-200' },
    { id: 'evenement',  label: 'Événement',   color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { id: 'autre',      label: 'Autre',       color: 'bg-slate-100 text-slate-600 border-slate-200' },
];

export function getCategoryStyle(id) {
    return NOTE_CATEGORIES.find(c => c.id === id) || NOTE_CATEGORIES[NOTE_CATEGORIES.length - 1];
}

/**
 * Trigger icon + popover for managing notes attached to a (date, site_id?) pair.
 * `notes` are the pre-fetched notes for this date (all sites). The component
 * filters to ones matching either site_id or global (no site). On any change it
 * calls onChanged() so the parent reloads.
 */
export default function SalesNotePopover({
    dateIso,
    siteId,
    siteName,
    sites = [],
    notes = [],
    onChanged,
    size = 'sm',
    variant = 'icon', // 'icon' | 'button' (prominent CTA for headers/toolbars)
    label = '+ Nouvelle note',
}) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // {id?, text, category, site_id}
    const [saving, setSaving] = useState(false);

    // Filter notes relevant to this (date, site)
    const relevant = notes.filter(n => n.date === dateIso && (!siteId || !n.site_id || n.site_id === siteId));

    const startNew = () => {
        setEditing({ id: null, date: dateIso, text: '', category: 'autre', site_id: siteId || null });
    };

    const cancel = () => setEditing(null);

    const save = async () => {
        const t = (editing?.text || '').trim();
        if (!t) { toast.error('Texte vide'); return; }
        if (!editing.date) { toast.error('Date requise'); return; }
        setSaving(true);
        try {
            if (editing.id) {
                await salesAPI.updateNote(editing.id, {
                    text: t,
                    category: editing.category,
                    site_id: editing.site_id,
                    date: editing.date,
                });
                toast.success('Note mise à jour');
            } else {
                await salesAPI.createNote({
                    date: editing.date,
                    site_id: editing.site_id || null,
                    category: editing.category,
                    text: t,
                });
                toast.success('Note ajoutée');
            }
            setEditing(null);
            if (onChanged) onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm('Supprimer cette note ?')) return;
        try {
            await salesAPI.deleteNote(id);
            toast.success('Note supprimée');
            if (onChanged) onChanged();
        } catch (e) {
            toast.error('Erreur');
        }
    };

    const hasNotes = relevant.length > 0;
    const iconSize = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';

    // When opening the popover in "button" mode (CTA), jump directly into the
    // creation form unless there are existing notes to display.
    const handleOpen = (next) => {
        setOpen(next);
        if (next && variant === 'button' && !hasNotes && !editing) {
            startNew();
        }
        if (!next) setEditing(null);
    };

    return (
        <Popover open={open} onOpenChange={handleOpen}>
            <PopoverTrigger asChild>
                {variant === 'button' ? (
                    <Button
                        type="button"
                        size="sm"
                        className="h-7 px-2.5 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1"
                        data-testid={`note-trigger-button-${dateIso}-${siteId || 'global'}`}
                        title={hasNotes ? `${relevant.length} note(s) ce jour` : "Ajouter une note contextuelle"}
                    >
                        <StickyNote className="w-3.5 h-3.5" />
                        {label}
                        {hasNotes && (
                            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px] bg-white/30 text-white">
                                {relevant.length}
                            </Badge>
                        )}
                    </Button>
                ) : (
                    <button
                        type="button"
                        className={`inline-flex items-center justify-center rounded-full transition-colors ${size === 'xs' ? 'w-4 h-4' : 'w-5 h-5'} ${hasNotes ? 'text-amber-600 hover:bg-amber-100' : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100'}`}
                        title={hasNotes ? `${relevant.length} note(s)` : 'Ajouter une note'}
                        data-testid={`note-trigger-${dateIso}-${siteId || 'global'}`}
                    >
                        <StickyNote className={iconSize} />
                        {hasNotes && (
                            <span className="absolute -mt-3 ml-3 w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        )}
                    </button>
                )}
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[340px] p-0" data-testid="note-popover-content">
                <div className="px-3 py-2 border-b bg-slate-50">
                    <div className="flex items-center gap-2 text-xs text-slate-700">
                        <StickyNote className="w-3.5 h-3.5 text-amber-500" />
                        <span className="font-semibold">Notes du {dateIso}</span>
                        {siteName && <Badge variant="outline" className="text-[10px]">{siteName.replace('FE ', '')}</Badge>}
                    </div>
                </div>

                {/* Existing notes */}
                <div className="max-h-[260px] overflow-y-auto divide-y">
                    {relevant.length === 0 && !editing && (
                        <div className="px-3 py-4 text-center text-xs text-slate-400 italic">Aucune note</div>
                    )}
                    {relevant.map(n => {
                        const cat = getCategoryStyle(n.category);
                        const isEditing = editing?.id === n.id;
                        if (isEditing) return null;
                        return (
                            <div key={n.id} className="px-3 py-2 hover:bg-slate-50 group/note">
                                <div className="flex items-start gap-2">
                                    <Badge className={`text-[9px] font-semibold px-1.5 py-0 h-4 ${cat.color}`}>
                                        {cat.label}
                                    </Badge>
                                    {n.site_name ? (
                                        <Badge variant="outline" className="text-[9px] h-4">{n.site_name.replace('FE ', '')}</Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-[9px] h-4 text-slate-500">Tous sites</Badge>
                                    )}
                                    {n.source === 'ia' && (
                                        <Badge className="text-[9px] h-4 px-1.5 bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" title="Note pré-remplie automatiquement par Sokuzai (IA) — modifiable">IA</Badge>
                                    )}
                                    <div className="ml-auto flex items-center opacity-0 group-hover/note:opacity-100">
                                        <button
                                            type="button"
                                            onClick={() => setEditing({ id: n.id, date: n.date, text: n.text, category: n.category, site_id: n.site_id })}
                                            className="p-1 text-slate-400 hover:text-blue-500"
                                            data-testid={`note-edit-${n.id}`}
                                        ><Pencil className="w-3 h-3" /></button>
                                        <button
                                            type="button"
                                            onClick={() => del(n.id)}
                                            className="p-1 text-slate-400 hover:text-red-500"
                                            data-testid={`note-delete-${n.id}`}
                                        ><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                </div>
                                <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{n.text}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Edit / create form */}
                {editing ? (
                    <div className="border-t bg-slate-50 px-3 py-2 space-y-2">
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Date</label>
                                <Input
                                    type="date"
                                    value={editing.date || ''}
                                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                                    className="h-7 text-xs"
                                    data-testid="note-date-input"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Catégorie</label>
                                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                    <SelectTrigger className="h-7 text-xs" data-testid="note-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {NOTE_CATEGORIES.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {sites && sites.length > 0 && (
                            <div>
                                <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Site</label>
                                <Select
                                    value={editing.site_id || 'global'}
                                    onValueChange={(v) => setEditing({ ...editing, site_id: v === 'global' ? null : v })}
                                >
                                    <SelectTrigger className="h-7 text-xs" data-testid="note-site-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="global">Tous sites</SelectItem>
                                        {sites.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name.replace('FE ', '')}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <Textarea
                            value={editing.text}
                            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                            placeholder="ex: Fermeture exceptionnelle, panne frigo..."
                            className="text-xs min-h-[60px]"
                            autoFocus
                            data-testid="note-text-input"
                        />
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancel}>
                                <X className="w-3 h-3 mr-1" />Annuler
                            </Button>
                            <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600" onClick={save} disabled={saving} data-testid="note-save-btn">
                                <Save className="w-3 h-3 mr-1" />Enregistrer
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="border-t bg-slate-50 px-3 py-1.5">
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-slate-600 w-full" onClick={startNew} data-testid="note-add-btn">
                            <Plus className="w-3 h-3 mr-1" />Ajouter une note
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
